import { useEffect, useRef, useState, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Box, Slider, Typography, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import * as cornerstone from 'cornerstone-core';
import * as cornerstoneTools from 'cornerstone-tools';
import { enableViewportTools } from '../services/cornerstoneInit';
import { ReferenceLines } from '../services/referenceLines';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    flex: 1,
    minWidth: 0,
    border: '1px solid transparent',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease',
  },
  containerActive: {
    borderColor: '#ff0000 !important',
  },
  viewportWrapper: {
    position: 'relative',
    flex: 1,
    backgroundColor: '#000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Keep a tiny gutter so the canvas never visually touches the active border
    padding: theme.spacing(0.25),
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  viewportInner: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  viewportWrapperClickable: {
    cursor: 'pointer',
  },
  viewport: {
    width: '100%',
    height: '100%',
  },
  overlayCanvas: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
  },
  overlay: {
    position: 'absolute',
    color: '#fff',
    pointerEvents: 'none',
    fontSize: '13px',
    textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
  },
  topLeft: {
    top: 5,
    left: 5,
  },
  topRight: {
    top: 5,
    right: 5,
    textAlign: 'right',
  },
  bottomLeft: {
    bottom: 5,
    left: 5,
  },
  bottomRight: {
    bottom: 5,
    right: 5,
    textAlign: 'right',
  },
  topCenter: {
    top: 5,
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: '15px',
  },
  bottomCenter: {
    bottom: 5,
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: '15px',
  },
  leftCenter: {
    top: '50%',
    left: 5,
    transform: 'translateY(-50%)',
    fontSize: '15px',
  },
  rightCenter: {
    top: '50%',
    right: 5,
    transform: 'translateY(-50%)',
    fontSize: '15px',
  },
  sliderContainer: {
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  slider: {
    flex: 1,
  },
  title: {
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const getDirectionalMarkers = (imageOrientationPatient) => {
  // If no IOP provided, return empty markers
  if (!imageOrientationPatient) {
    return { top: '', bottom: '', left: '', right: '' };
  }

  // Parse IOP string to array of floats
  let iop;
  if (typeof imageOrientationPatient === 'string') {
    iop = imageOrientationPatient.split('\\').map(parseFloat);
  } else if (Array.isArray(imageOrientationPatient)) {
    iop = imageOrientationPatient;
  } else {
    return { top: '', bottom: '', left: '', right: '' };
  }

  if (iop.length !== 6) {
    return { top: '', bottom: '', left: '', right: '' };
  }

  const rowCosines = [iop[0], iop[1], iop[2]];
  const colCosines = [iop[3], iop[4], iop[5]];

  // Calculate cross product to get slice normal
  const normal = [
    rowCosines[1] * colCosines[2] - rowCosines[2] * colCosines[1],
    rowCosines[2] * colCosines[0] - rowCosines[0] * colCosines[2],
    rowCosines[0] * colCosines[1] - rowCosines[1] * colCosines[0],
  ];

  // Find dominant axis
  const absNormal = normal.map(Math.abs);
  const maxIndex = absNormal.indexOf(Math.max(...absNormal));

  // Determine orientation based on dominant axis and return appropriate markers
  if (maxIndex === 0) {
    // SAGITTAL
    return { top: 'S', bottom: 'I', left: 'A', right: 'P' };
  } else if (maxIndex === 1) {
    // CORONAL
    return { top: 'S', bottom: 'I', left: 'R', right: 'L' };
  } else if (maxIndex === 2) {
    // AXIAL
    return { top: 'A', bottom: 'P', left: 'R', right: 'L' };
  }

  return { top: '', bottom: '', left: '', right: '' };
};

const CornerstoneViewport = forwardRef(({ 
  imageIds = [], 
  orientation = 'UNKNOWN',
  seriesOrientation = null,
  activeTool = 'no-tool',
  seriesInstanceUID = null,
  seriesDescription = '',
  referenceLinesEnabled = false,
  viewportData = {},
  onImageIndexChange,
  isActive = false,
  onViewportClick,
  activeViewport = null,
  patientName = '',
  dateOfBirth = '',
  seriesDate = '',
  seriesTime = '',
}, ref) => {
  const classes = useStyles();
  const viewportRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const [currentSlice, setCurrentSlice] = useState(0);
  const [viewportInfo, setViewportInfo] = useState({
    zoom: 1,
    windowWidth: 0,
    windowCenter: 0,
  });
  
  // Reference lines for this viewport
  const referenceLinesRefs = useRef({
    sagittal: new ReferenceLines(),
    axial: new ReferenceLines(),
    coronal: new ReferenceLines(),
  });

  // Store current image for reference lines
  const currentImageRef = useRef(null);

  // Store initial WW/WL (VOI) so we can reset later
  const initialVoiRef = useRef(null);
  const hasCapturedInitialVoiRef = useRef(false);
  
  // Track the current drawing request to prevent race conditions
  const drawingRequestRef = useRef(0);

  // Series-keyed history: { seriesUID: { history: [...], index: number } }
  const historyMapRef = useRef({});
  const currentSeriesUIDRef = useRef(null);
  const isCreatingAnnotationRef = useRef(false);  // Track if actively creating new annotation
  const isModifyingAnnotationRef = useRef(false);  // Track if actively modifying existing annotation

  // Cache loaded source images to avoid async flicker while scrolling
  const sourceImageCacheRef = useRef(new Map());

  // Wrap-around wheel scrolling
  const wheelHandlerRef = useRef(null);
  const wheelScrollRafRef = useRef(null);

  // Refs to avoid stale closures inside Cornerstone DOM event listeners
  const referenceLinesEnabledRef = useRef(referenceLinesEnabled);
  const drawAllReferenceLinesRef = useRef(null);

  // Directional markers computed from current image's IOP (updated dynamically)
  const [directionalMarkers, setDirectionalMarkers] = useState({
    top: '', bottom: '', left: '', right: ''
  });

  // Update directional markers from the current image's IOP
  const updateDirectionalMarkersFromImage = useCallback((element) => {
    if (!element) return;

    try {
      const enabledElement = cornerstone.getEnabledElement(element);
      if (!enabledElement || !enabledElement.image) return;

      const image = enabledElement.image;
      
      // Try to get IOP from the image data
      let iop = null;
      
      // Try image.data (DICOM dataset)
      if (image.data && image.data.string) {
        try {
          iop = image.data.string('x00200037');
        } catch (e) {
          // IOP not available
        }
      }

      if (iop) {
        const markers = getDirectionalMarkers(iop);
        setDirectionalMarkers(markers);
      }
    } catch (error) {
      // Silently fail - markers will stay empty
    }
  }, []);

  const applyActiveToolForThisViewport = useCallback((element, toolKey) => {
    if (!element) return;

    const toolMap = {
      'no-tool': null,
      'pan': 'Pan',
      'zoom': 'Zoom',
      'ww/wl': 'Wwwc',
      'distance': 'Length',
      'angle': 'Angle',
      'cobb-angle': 'CobbAngle',
      'text': 'ArrowAnnotate',
    };

    const selected = toolMap[toolKey];

    // Make all draw tools passive first (so only one is active)
    ['Pan', 'Zoom', 'Wwwc', 'Length', 'Angle', 'CobbAngle', 'ArrowAnnotate'].forEach((t) => {
      try { cornerstoneTools.setToolPassiveForElement(element, t); } catch (e) {}
    });

    if (!selected) return;

    try {
      cornerstoneTools.setToolActiveForElement(element, selected, { mouseButtonMask: 1 });
    } catch (e) {
      // If tools aren't registered yet, ignore
    }
  }, []);

  // Redraw the current image to clear any previously drawn reference lines
  const clearReferenceLines = () => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (context) {
      context.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  // Draw reference lines from a specific source
  const drawReferenceLinesFrom = (sourceOrientation, requestId) => {
    const element = viewportRef.current;
    if (!element || !currentImageRef.current || !referenceLinesEnabled) return;
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;

    const sourceData = viewportData[sourceOrientation];
    if (!sourceData || sourceData.imageIds.length === 0) return;

    // Load the current image from the source viewport
    const sourceImageId = sourceData.imageIds[sourceData.currentImageIndex || 0];
    if (!sourceImageId) return;

    const syncOverlayCanvasSize = () => {
      const baseCanvas = element.querySelector('canvas');
      const overlayCanvas = overlayCanvasRef.current;
      if (baseCanvas && overlayCanvas) {
        // Setting width/height clears the canvas; only do it if changed
        if (overlayCanvas.width !== baseCanvas.width) {
          overlayCanvas.width = baseCanvas.width;
        }
        if (overlayCanvas.height !== baseCanvas.height) {
          overlayCanvas.height = baseCanvas.height;
        }
      }
    };

    const drawWithSourceImage = (sourceImage) => {
      if (requestId !== drawingRequestRef.current) return;
      if (!currentImageRef.current) return;

      const refLines = referenceLinesRefs.current[sourceOrientation];
      const success = refLines.build(sourceImage, currentImageRef.current);
      if (!success) return;

      syncOverlayCanvasSize();
      // draw() clears only if it can compute a valid line
      refLines.draw(canvas, element);
    };

    const cached = sourceImageCacheRef.current.get(sourceImageId);
    if (cached) {
      drawWithSourceImage(cached);
      return;
    }

    cornerstone
      .loadImage(sourceImageId)
      .then((sourceImage) => {
        sourceImageCacheRef.current.set(sourceImageId, sourceImage);
        drawWithSourceImage(sourceImage);
      })
      .catch(() => {
        // Silently fail - not all images may have proper metadata
      });
  };

  // Draw reference lines only from the active viewport
  const drawAllReferenceLines = () => {
    // Increment request ID to invalidate any pending async operations
    drawingRequestRef.current += 1;
    const currentRequestId = drawingRequestRef.current;

    if (!referenceLinesEnabled) return;

    // Note: do NOT clear immediately here. If we clear and then the async
    // source image load gets cancelled by rapid scrolling, the overlay stays
    // blank. We clear right before drawing once the new line is ready.

    // Only draw reference lines if there's an active viewport
    // and this viewport is NOT the active one
    const currentOrientation = orientation.toLowerCase();
    
    if (!activeViewport) {
      // No active viewport selected, don't draw any reference lines
      clearReferenceLines();
      return;
    }
    
    if (activeViewport === currentOrientation) {
      // This IS the active viewport, don't draw reference lines on itself
      clearReferenceLines();
      return;
    }
    
    // Draw reference lines only from the active viewport
    drawReferenceLinesFrom(activeViewport, currentRequestId); // NEW: Pass request ID
  };

  // Get history for current series
  const getCurrentHistory = useCallback(() => {
    const seriesUID = currentSeriesUIDRef.current || 'default';
    if (!historyMapRef.current[seriesUID]) {
      historyMapRef.current[seriesUID] = {
        history: [],
        index: -1,
      };
    }
    return historyMapRef.current[seriesUID];
  }, []);

  // Capture current annotation state for undo/redo
  const captureAnnotationSnapshot = useCallback(() => {
    const element = viewportRef.current;
    if (!element) return null;

    // Get the stack to know which imageIds belong to this viewport
    const stackState = cornerstoneTools.getToolState(element, 'stack');
    const stack = stackState?.data?.[0];
    const stackImageIds = Array.isArray(stack?.imageIds) ? stack.imageIds : [];
    if (stackImageIds.length === 0) return null;

    // Access the global tool state manager
    const mgr =
      cornerstoneTools.globalImageIdSpecificToolStateManager ||
      cornerstoneTools.store?.state?.globalImageIdSpecificToolStateManager;

    let root = null;
    if (mgr) {
      root =
        mgr.toolState ||
        mgr._toolState ||
        mgr._imageIdToolState ||
        mgr._toolStateMap ||
        null;
    }

    if (!root) return null;

    // Tools we're tracking
    const toolsToSnapshot = ['Length', 'Angle', 'CobbAngle', 'ArrowAnnotate'];

    // Build snapshot keyed by slice INDEX (stable) instead of imageId (unstable)
    const snapshot = { __meta__: { stackLength: stackImageIds.length } };
    
    stackImageIds.forEach((imageId, sliceIndex) => {
      const entry = root[imageId];
      if (!entry) return;

      const imageSnapshot = {};
      toolsToSnapshot.forEach((toolName) => {
        if (entry[toolName] && entry[toolName].data) {
          // Deep clone the tool data
          imageSnapshot[toolName] = JSON.parse(JSON.stringify(entry[toolName].data));
        }
      });

      // Only add to snapshot if this slice has annotations
      if (Object.keys(imageSnapshot).length > 0) {
        // Use slice index as key (e.g., "slice_0", "slice_5")
        snapshot[`slice_${sliceIndex}`] = imageSnapshot;
      }
    });

    return snapshot;
  }, []);

  // Restore annotation state from a snapshot
  const restoreAnnotationSnapshot = useCallback((snapshot) => {
    const element = viewportRef.current;
    if (!element || !snapshot) return;

    // Get the stack
    const stackState = cornerstoneTools.getToolState(element, 'stack');
    const stack = stackState?.data?.[0];
    const stackImageIds = Array.isArray(stack?.imageIds) ? stack.imageIds : [];
    if (stackImageIds.length === 0) return;

    // If snapshot was captured on a different stack length, slice-index mapping is unsafe
    const expectedLen = snapshot?.__meta__?.stackLength;
    if (Number.isFinite(expectedLen) && expectedLen !== stackImageIds.length) {
      console.warn(
        '[History] Skip restore (stack length mismatch). expected=',
        expectedLen,
        'actual=',
        stackImageIds.length
      );
      return;
    }

    // Access the global tool state manager
    const mgr =
      cornerstoneTools.globalImageIdSpecificToolStateManager ||
      cornerstoneTools.store?.state?.globalImageIdSpecificToolStateManager;

    let root = null;
    if (mgr) {
      root =
        mgr.toolState ||
        mgr._toolState ||
        mgr._imageIdToolState ||
        mgr._toolStateMap ||
        null;
    }

    if (!root) return;

    const toolsToRestore = ['Length', 'Angle', 'CobbAngle', 'ArrowAnnotate'];

    // Clear all existing annotations for this viewport's imageIds
    stackImageIds.forEach((imageId) => {
      const entry = root[imageId];
      if (!entry) return;

      toolsToRestore.forEach((toolName) => {
        if (entry[toolName]) {
          delete entry[toolName];
        }
      });

      // Remove empty imageId buckets
      if (Object.keys(entry).length === 0) {
        delete root[imageId];
      }
    });

    // Restore annotations from snapshot using slice index to map to current imageIds
    Object.keys(snapshot).forEach((snapshotKey) => {
      if (snapshotKey === '__meta__') return;

      // Extract slice index from key (e.g., "slice_0" -> 0)
      const match = snapshotKey.match(/^slice_(\d+)$/);
      if (!match) return;
      
      const sliceIndex = parseInt(match[1], 10);
      
      // Get the current imageId for this slice index
      const targetImageId = stackImageIds[sliceIndex];
      if (!targetImageId) {
        console.warn('[History] Slice index out of bounds:', sliceIndex);
        return;
      }

      const imageSnapshot = snapshot[snapshotKey];

      // Ensure entry exists for this imageId
      if (!root[targetImageId]) {
        root[targetImageId] = {};
      }

      // Restore each tool's data
      Object.keys(imageSnapshot).forEach((toolName) => {
        if (toolsToRestore.includes(toolName)) {
          root[targetImageId][toolName] = {
            data: JSON.parse(JSON.stringify(imageSnapshot[toolName])),
          };
        }
      });
    });

    // Re-render to show restored annotations
    cornerstone.updateImage(element);
  }, []);

  // Push current annotation state to history
  const pushToHistory = useCallback(() => {
    const snapshot = captureAnnotationSnapshot();
    if (!snapshot) return;

    const current = getCurrentHistory();
    
    // Truncate history after current index (clears redo stack)
    current.history = current.history.slice(0, current.index + 1);

    // Add new snapshot
    current.history.push(snapshot);
    current.index = current.history.length - 1;

    console.log('[History] Pushed snapshot. Series:', currentSeriesUIDRef.current, 'Index:', current.index, 'Length:', current.history.length);
  }, [captureAnnotationSnapshot, getCurrentHistory]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    updateReferenceLinesFromOther: () => {
      drawAllReferenceLines();
    },
    resizeViewport: () => {
      const element = viewportRef.current;
      if (!element) return;

      try {
        // Only resize if cornerstone has enabled the element
        cornerstone.getEnabledElement(element);
        cornerstone.resize(element, false); // false = preserve zoom/pan state
      } catch (error) {
        // Ignore - element may not be enabled yet
      }
    },
    
    getElement: () => {
      return viewportRef.current;
    },

    // Clear all annotations and push to history
    clearAnnotations: () => {
      const element = viewportRef.current;
      if (!element) return;

      // Get all imageIds in the stack for this viewport
      const stackState = cornerstoneTools.getToolState(element, 'stack');
      const stack = stackState?.data?.[0];
      const imageIds = Array.isArray(stack?.imageIds) ? stack.imageIds : [];

      const toolsToClear = ['Length', 'Angle', 'CobbAngle', 'ArrowAnnotate'];

      // Clear annotations across all slices
      const mgr =
        cornerstoneTools.globalImageIdSpecificToolStateManager ||
        cornerstoneTools.store?.state?.globalImageIdSpecificToolStateManager;

      let root = null;

      if (mgr) {
        root =
          mgr.toolState ||
          mgr._toolState ||
          mgr._imageIdToolState ||
          mgr._toolStateMap ||
          null;
      }

      if (root && imageIds.length > 0) {
        imageIds.forEach((imageId) => {
          const entry = root[imageId];
          if (!entry) return;

          toolsToClear.forEach((toolName) => {
            if (entry[toolName]) {
              delete entry[toolName];
            }
          });

          // Remove empty imageId buckets
          if (Object.keys(entry).length === 0) {
            delete root[imageId];
          }
        });
      } else {
        // Fallback: Clear only the current slice
        toolsToClear.forEach((toolName) => {
          try {
            cornerstoneTools.clearToolState(element, toolName);
          } catch (e) {}
        });
      }

      // Re-render
      cornerstone.updateImage(element);

      console.log('[History] Cleared all annotations');

      // Push the cleared state to history (undo will restore previous state)
      pushToHistory();
    },

    resetView: () => {
      const element = viewportRef.current;
      if (!element) return;

      // ------------------------------------------------------------------
      // 1) Reset WW/WL back to initial values
      // ------------------------------------------------------------------
      try {
        const vp = cornerstone.getViewport(element);
        if (vp && initialVoiRef.current && vp.voi) {
          vp.voi = {
            ...vp.voi,
            windowWidth: initialVoiRef.current.windowWidth,
            windowCenter: initialVoiRef.current.windowCenter,
          };
          cornerstone.setViewport(element, vp);
        }
      } catch (e) {}

      // ------------------------------------------------------------------
      // 2) Reset zoom + pan to fill viewport
      // ------------------------------------------------------------------
      try {
        cornerstone.fitToWindow(element);
      } catch (e) {}

      // ------------------------------------------------------------------
      // 3) Re-render
      // ------------------------------------------------------------------
      cornerstone.updateImage(element);
    },

    // Undo last annotation action
    undo: () => {
      const current = getCurrentHistory();
      if (current.index <= 0) return; // Nothing to undo

      current.index -= 1;
      const snapshot = current.history[current.index];
      restoreAnnotationSnapshot(snapshot);
      
      console.log('[History] Undo to index:', current.index, 'Series:', currentSeriesUIDRef.current);
    },

    // Redo previously undone action
    redo: () => {
      const current = getCurrentHistory();
      if (current.index >= current.history.length - 1) return; // Nothing to redo

      current.index += 1;
      const snapshot = current.history[current.index];
      restoreAnnotationSnapshot(snapshot);
      
      console.log('[History] Redo to index:', current.index, 'Series:', currentSeriesUIDRef.current);
    },

    // Check if undo is available
    canUndo: () => {
      const current = getCurrentHistory();
      return current.index > 0;
    },

    // Check if redo is available
    canRedo: () => {
      const current = getCurrentHistory();
      return current.index < current.history.length - 1;
    },

    // Check if any annotations exist in current viewport
    hasAnnotations: () => {
      const element = viewportRef.current;
      if (!element) return false;

      const stackState = cornerstoneTools.getToolState(element, 'stack');
      const stack = stackState?.data?.[0];
      const imageIds = Array.isArray(stack?.imageIds) ? stack.imageIds : [];
      if (imageIds.length === 0) return false;

      const mgr =
        cornerstoneTools.globalImageIdSpecificToolStateManager ||
        cornerstoneTools.store?.state?.globalImageIdSpecificToolStateManager;

      let root = null;
      if (mgr) {
        root =
          mgr.toolState ||
          mgr._toolState ||
          mgr._imageIdToolState ||
          mgr._toolStateMap ||
          null;
      }

      if (!root) return false;

      const toolsToCheck = ['Length', 'Angle', 'CobbAngle', 'ArrowAnnotate'];

      // Check if any imageId has any annotation data
      for (const imageId of imageIds) {
        const entry = root[imageId];
        if (!entry) continue;

        for (const toolName of toolsToCheck) {
          if (entry[toolName] && entry[toolName].data && entry[toolName].data.length > 0) {
            return true;
          }
        }
      }

      return false;
    },

    // Check if actively creating a new annotation
    isCreating: () => {
      return isCreatingAnnotationRef.current;
    },
  }));

  // Keep refs in sync for stable event listeners
  useEffect(() => {
    referenceLinesEnabledRef.current = referenceLinesEnabled;
  }, [referenceLinesEnabled]);

  // Expose the latest draw function to stable event listeners
  drawAllReferenceLinesRef.current = drawAllReferenceLines;

  const updateViewportData = (element) => {
    try {
      const viewport = cornerstone.getViewport(element);
      const image = cornerstone.getImage(element);
      
      if (viewport && image) {
        setViewportInfo({
          zoom: viewport.scale.toFixed(2),
          windowWidth: Math.round(viewport.voi.windowWidth),
          windowCenter: Math.round(viewport.voi.windowCenter),
        });
      }
    } catch (error) {
      // Ignore errors during viewport updates
    }
  };

  const onImageRendered = (e) => {
    const element = e.target;
    updateViewportData(element);

    // Ensure currentImageRef matches the image that was actually displayed
    try {
      const displayedImage = cornerstone.getImage(element);
      if (displayedImage) {
        currentImageRef.current = displayedImage;
        // Update directional markers when image renders
        updateDirectionalMarkersFromImage(element);
      }
    } catch (error) {
      // Ignore
    }

    // Ensure overlay canvas matches the underlying image canvas size
    const baseCanvas = element.querySelector('canvas');
    const overlayCanvas = overlayCanvasRef.current;
    if (baseCanvas && overlayCanvas) {
      // Only resize when size changed; resizing clears the overlay
      if (overlayCanvas.width !== baseCanvas.width) {
        overlayCanvas.width = baseCanvas.width;
      }
      if (overlayCanvas.height !== baseCanvas.height) {
        overlayCanvas.height = baseCanvas.height;
      }
    }

    // Redraw reference lines after the image is rendered. Otherwise the overlay
    // can be cleared by canvas resize and remain blank.
    if (referenceLinesEnabledRef.current && drawAllReferenceLinesRef.current) {
      requestAnimationFrame(() => {
        if (referenceLinesEnabledRef.current && drawAllReferenceLinesRef.current) {
          drawAllReferenceLinesRef.current();
        }
      });
    }
  };

  const onStackScroll = () => {
    const element = viewportRef.current;
    if (!element) return;

    const stackData = cornerstoneTools.getToolState(element, 'stack');
    if (stackData && stackData.data && stackData.data[0]) {
      const newIndex = stackData.data[0].currentImageIdIndex;
      setCurrentSlice(newIndex);
      
      // Load new image and update reference
      cornerstone.loadImage(imageIds[newIndex]).then((image) => {
        currentImageRef.current = image;
        
        // Notify parent of image index change
        if (onImageIndexChange) {
          onImageIndexChange(newIndex);
        }
      }).catch(error => {
        console.error('Error loading image:', error);
      });
    }
  };

  useEffect(() => {
    const element = viewportRef.current;
    if (!element || imageIds.length === 0) return;

    // Reset UI state when a new series/stack is loaded
    setCurrentSlice(0);
    if (onImageIndexChange) onImageIndexChange(0);

    // Enable the element for cornerstone
    cornerstone.enable(element);

    // Keep cornerstone canvas in sync with layout changes (e.g., when COR viewport is toggled)
    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        // Defer resize to next frame to avoid resize/layout thrash
        requestAnimationFrame(() => {
          try {
            cornerstone.getEnabledElement(element);
            cornerstone.resize(element, false); // false = preserve zoom/pan state
          } catch (error) {
            // Ignore
          }
        });
      });

      resizeObserver.observe(element);
    }

    // Load the first image
    cornerstone.loadImage(imageIds[0]).then((image) => {
      currentImageRef.current = image;
      cornerstone.displayImage(element, image);

      // Update directional markers from loaded image
      updateDirectionalMarkersFromImage(element);

      // Ensure overlay + slider match what we just displayed
      setCurrentSlice(0);
      if (onImageIndexChange) onImageIndexChange(0);

      // Capture initial WW/WL (VOI) once per viewport (for Reset Image)
      if (!hasCapturedInitialVoiRef.current) {
        try {
          const vp = cornerstone.getViewport(element);
          if (vp?.voi) {
            initialVoiRef.current = {
              windowWidth: vp.voi.windowWidth,
              windowCenter: vp.voi.windowCenter,
            };
            hasCapturedInitialVoiRef.current = true;
          }
        } catch (e) {
          // Ignore
        }
      }


      // Set up the stack
      const stack = {
        currentImageIdIndex: 0,
        imageIds: imageIds,
      };
      cornerstoneTools.addStackStateManager(element, ['stack']);
      cornerstoneTools.addToolState(element, 'stack', stack);

      // Enable viewport tools
      enableViewportTools(element);

      // Re-apply currently selected tool for this viewport
      applyActiveToolForThisViewport(element, activeTool);

      // Handle history initialization/restoration AFTER stack is ready
      const seriesUID = seriesInstanceUID; // Use the prop directly
      if (seriesUID) {
        if (!historyMapRef.current[seriesUID]) {
          // New series - capture baseline (will be empty since we just loaded)
          const baseline = captureAnnotationSnapshot();
          historyMapRef.current[seriesUID] = {
            history: [baseline || {}],
            index: 0,
          };
          console.log('[History] Initialized new series in main effect:', seriesUID);
        } else {
          // Existing series - restore its annotations NOW that stack is ready
          const historyData = historyMapRef.current[seriesUID];
          const snapshot = historyData.history[historyData.index];
          const hasSliceKeys = snapshot && Object.keys(snapshot).some((k) => k.startsWith('slice_'));
          if (hasSliceKeys) {
            restoreAnnotationSnapshot(snapshot);
            console.log('[History] Restored series in main effect:', seriesUID,
                        'Index:', historyData.index, 'Length:', historyData.history.length);
          }
        }
        currentSeriesUIDRef.current = seriesUID;
      }

      // Update viewport data
      updateViewportData(element);

      // Listen to image rendered event
      element.addEventListener('cornerstoneimagerendered', onImageRendered);

      // Listen to stack scroll event
      element.addEventListener('cornerstonetoolsstackscroll', onStackScroll);
    }).catch(error => {
      console.error('Error initializing viewport:', error);
    });

    return () => {
      // Cleanup: Remove event listeners and disable element
      if (element) {
        element.removeEventListener('cornerstoneimagerendered', onImageRendered);
        element.removeEventListener('cornerstonetoolsstackscroll', onStackScroll);

        if (resizeObserver) {
          try {
            resizeObserver.disconnect();
          } catch (error) {
            // Ignore
          }
        }
        
        // Clear cornerstone element
        try {
          cornerstone.disable(element);
        } catch (error) {
          console.warn('Error disabling viewport:', error);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageIds, seriesInstanceUID, captureAnnotationSnapshot, restoreAnnotationSnapshot]);

  useEffect(() => {
    const element = viewportRef.current;
    if (!element) return;

    // Only apply if cornerstone has already enabled this element
    try {
      cornerstone.getEnabledElement(element);
    } catch (e) {
      return;
    }

    applyActiveToolForThisViewport(element, activeTool);
  }, [activeTool, applyActiveToolForThisViewport]);

  // Capture annotation history on creation/modification
  useEffect(() => {
    const element = viewportRef.current;
    if (!element || imageIds.length === 0) return;

    // Handler for when a NEW annotation starts being created
    const handleMeasurementAdded = (evt) => {
      const toolType = evt?.detail?.toolType;
      
      // Only track actual annotation tools, ignore stack/zoom/pan/etc.
      const annotationTools = ['Length', 'Angle', 'CobbAngle', 'ArrowAnnotate'];
      if (!annotationTools.includes(toolType)) {
        return;
      }
      
      console.log('[History] Annotation started');
      isCreatingAnnotationRef.current = true;
    };

    // Handler for completed annotations (new creation)
    const handleMeasurementCompleted = () => {
      console.log('[History] Annotation completed');
      isCreatingAnnotationRef.current = false; // Creation done
      
      pushToHistory()
    };

    // Handler for modified annotations (drag/resize EXISTING annotations)
    const handleMeasurementModified = () => {
      // Ignore modifications during initial creation
      if (isCreatingAnnotationRef.current) {
        return;
      }

      // Mark that a modification is in progress
      if (!isModifyingAnnotationRef.current) {
        console.log('[History] Edit started');
        isModifyingAnnotationRef.current = true;
      }
    };

    // Handler for mouse release - captures modification only when gesture completes
    const handleMouseUp = () => {
      if (isModifyingAnnotationRef.current) {
        console.log('[History] Edit completed');
        isModifyingAnnotationRef.current = false;
        pushToHistory();
      }
    };

    // Listen to annotation events
    element.addEventListener('cornerstonetoolsmeasurementadded', handleMeasurementAdded);
    element.addEventListener('cornerstonetoolsmeasurementcompleted', handleMeasurementCompleted);
    element.addEventListener('cornerstonetoolsmeasurementmodified', handleMeasurementModified);
    element.addEventListener('mouseup', handleMouseUp);

    return () => {
      if (element) {
        element.removeEventListener('cornerstonetoolsmeasurementadded', handleMeasurementAdded);
        element.removeEventListener('cornerstonetoolsmeasurementcompleted', handleMeasurementCompleted);
        element.removeEventListener('cornerstonetoolsmeasurementmodified', handleMeasurementModified);
        element.removeEventListener('mouseup', handleMouseUp);
      }
      
      // Reset creation flag
      isCreatingAnnotationRef.current = false;
    };
  }, [imageIds, captureAnnotationSnapshot, pushToHistory, getCurrentHistory]);

  // Handle series changes - just track the UID (restoration happens in main useEffect)
  useEffect(() => {
    if (seriesInstanceUID && seriesInstanceUID !== currentSeriesUIDRef.current) {
      console.log('[History] Series UID changed to:', seriesInstanceUID);
      // Don't restore here - it happens in the main useEffect after stack is ready
    }
  }, [seriesInstanceUID]);

  // Wrap-around wheel scrolling
  useEffect(() => {
    const element = viewportRef.current;
    if (!element || imageIds.length <= 1) return;

    wheelHandlerRef.current = (evt) => {
      const el = viewportRef.current;
      if (!el || imageIds.length <= 1) return;

      // prevent the page from scrolling
      evt.preventDefault();

      // throttle to 1 update per animation frame
      if (wheelScrollRafRef.current) return;
      wheelScrollRafRef.current = requestAnimationFrame(() => {
        wheelScrollRafRef.current = null;

        // read current stack index
        const stackState = cornerstoneTools.getToolState(el, 'stack');
        const stack = stackState?.data?.[0];
        if (!stack || !Array.isArray(stack.imageIds) || stack.imageIds.length === 0) return;

        const len = stack.imageIds.length;
        const dir = evt.deltaY > 0 ? 1 : -1;

        let nextIndex = (stack.currentImageIdIndex ?? 0) + dir;

        // WRAP AROUND
        if (nextIndex < 0) nextIndex = len - 1;
        if (nextIndex >= len) nextIndex = 0;

        if (nextIndex === stack.currentImageIdIndex) return;

        // update stack + display image
        stack.currentImageIdIndex = nextIndex;

        cornerstone
          .loadImage(imageIds[nextIndex])
          .then((img) => {
            currentImageRef.current = img;
            cornerstone.displayImage(el, img);

            setCurrentSlice(nextIndex);
            if (onImageIndexChange) onImageIndexChange(nextIndex);
            
            // Update directional markers from new image
            updateDirectionalMarkersFromImage(el);
          })
          .catch(() => {});
      });
    };

    // IMPORTANT: passive:false so preventDefault works
    element.addEventListener('wheel', wheelHandlerRef.current, { passive: false });

    return () => {
      if (wheelHandlerRef.current) {
        element.removeEventListener('wheel', wheelHandlerRef.current);
      }
      if (wheelScrollRafRef.current) cancelAnimationFrame(wheelScrollRafRef.current);
      wheelScrollRafRef.current = null;
      wheelHandlerRef.current = null;
    };
  }, [imageIds, onImageIndexChange, updateDirectionalMarkersFromImage]);

  // Redraw reference lines when enabled/disabled, viewport data changes, OR activeViewport changes
  useEffect(() => {
    if (referenceLinesEnabled && imageIds.length > 0 && currentImageRef.current) {
      drawAllReferenceLines();
    } else {
      clearReferenceLines();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceLinesEnabled, viewportData, activeViewport, imageIds.length]);

  const handleSliceChange = (event, newValue) => {
    const element = viewportRef.current;
    if (!element) return;

    const stackData = cornerstoneTools.getToolState(element, 'stack');
    if (stackData && stackData.data && stackData.data[0]) {
      stackData.data[0].currentImageIdIndex = newValue;
      cornerstone.loadImage(imageIds[newValue]).then((image) => {
        currentImageRef.current = image;
        cornerstone.displayImage(element, image);
        setCurrentSlice(newValue);
        
        // Update directional markers from new image
        updateDirectionalMarkersFromImage(element);
        
        // Notify parent of image index change
        if (onImageIndexChange) {
          onImageIndexChange(newValue);
        }
      }).catch(error => {
        console.error('Error changing slice:', error);
      });
    }
  };

  const handleViewportWrapperClick = () => {
    if (imageIds.length > 0 && onViewportClick) {
      onViewportClick();
    }
  };

  const hasImages = imageIds.length > 0;

  if (!hasImages) {
    return (
      <Paper className={classes.container}>
        <Box className={classes.title}>
          <Typography variant="subtitle2">
            {seriesOrientation ? seriesOrientation.toUpperCase() : orientation} – {seriesDescription || 'No series loaded'}
          </Typography>
        </Box>
        
        <Box className={classes.viewportWrapper}>
          <Typography color="textSecondary">
            No images to display
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper className={`${classes.container} ${isActive ? classes.containerActive : ''}`}>
      <Box className={classes.title}>
        <Typography variant="subtitle2">
          {seriesOrientation ? seriesOrientation.toUpperCase() : orientation} – {seriesDescription}
        </Typography>
      </Box>
      
      <Box 
        className={`${classes.viewportWrapper} ${hasImages ? classes.viewportWrapperClickable : ''}`}
        onClick={handleViewportWrapperClick}
      >
        <div className={classes.viewportInner}>
          <div ref={viewportRef} className={classes.viewport} />

          {/* Reference lines overlay canvas */}
          <canvas ref={overlayCanvasRef} className={`${classes.viewport} ${classes.overlayCanvas}`} />
        </div>
        
        {/* Overlays */}
        <Box className={`${classes.overlay} ${classes.topLeft}`}>
          <div>{patientName}</div>
          <div>DOB: {dateOfBirth}</div>
          <div>{seriesDate} {seriesTime}</div>
        </Box>
        
        <Box className={`${classes.overlay} ${classes.bottomRight}`}>
          <div>Slice: {currentSlice + 1} / {imageIds.length}</div>
          <div>Zoom: {viewportInfo.zoom}</div>
          <div>WL: {viewportInfo.windowCenter}&nbsp; WW: {viewportInfo.windowWidth}</div>
        </Box>

        {/* Directional markers */}
        <Box className={`${classes.overlay} ${classes.topCenter}`}>
          {directionalMarkers.top}
        </Box>
        <Box className={`${classes.overlay} ${classes.bottomCenter}`}>
          {directionalMarkers.bottom}
        </Box>
        <Box className={`${classes.overlay} ${classes.leftCenter}`}>
          {directionalMarkers.left}
        </Box>
        <Box className={`${classes.overlay} ${classes.rightCenter}`}>
          {directionalMarkers.right}
        </Box>
      </Box>

      {/* Slice Slider */}
      <Box className={classes.sliderContainer}>
        <Typography variant="caption">Slice:</Typography>
        <Slider
          className={classes.slider}
          value={currentSlice}
          min={0}
          max={imageIds.length - 1}
          onChange={handleSliceChange}
          valueLabelDisplay="off"
          valueLabelFormat={(value) => `${value + 1}/${imageIds.length}`}
        />
        <Typography variant="caption">
          {currentSlice + 1}/{imageIds.length}
        </Typography>
      </Box>
    </Paper>
  );
});

export default CornerstoneViewport;